import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getMission, getAvailableChauffeurs, getAvailableVehicles, getAvailableRemorques, assignMissionResources, getAssignedMissions, getMissionAssignment, getMissionAssignmentHistory, getMissionMobileData, analyzeMission, triggerAISuggestion, getAIProposals, decideAIProposal, uploadMissionDocument, updateMission, updateMissionStatus, isChauffeurActiveOnAnotherMission } from '../../services/missionService';
import api from '../../services/api';
import { getFileUrl } from '../../config';
import { Alert, Spinner, Button, Form, Card, Table, Row, Col, Badge, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaUserTie, FaCar, FaArrowLeft, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaGasPump, FaClipboardCheck, FaRoute, FaPencilAlt, FaTrailer, FaBrain, FaLightbulb, FaRobot, FaTimes, FaPhone, FaUpload, FaClock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useI18n } from '../../i18n/I18nContext';
import { useFilter } from '../../context/FilterContext';
import { getChauffeursPositions } from '../../services/positionService';
import { useEtaCalculator } from '../../hooks/useEtaCalculator';
import '../agent/AgentControlCenter.css';
import './MissionTimeline.css';
import PageHeader from '../../components/common/PageHeader';

const INITIAL_MOBILE_DATA = {
  documents: [],
  incidents: [],
  fuelDeclarations: [],
  vehicleInspections: []
};

const STATUS_VARIANTS = {
  EN_COURS: { badge: 'success', textClass: 'text-success' },
  EN_ATTENTE: { badge: 'warning', textClass: 'text-warning' },
  TERMINEE: { badge: 'secondary', textClass: 'text-secondary' },
  ANNULEE: { badge: 'danger', textClass: 'text-danger' }
};

function AssignMission() {
  const navigate = useNavigate();
  const { missionId: routeMissionId } = useParams();
  const location = useLocation();
  const missionId = routeMissionId || new URLSearchParams(location.search).get('missionId');
  const { t } = useI18n();
  const { selectedTransporteurId } = useFilter();

  const [mission, setMission] = useState(null);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [remorques, setRemorques] = useState([]);
  const [selectedChauffeur, setSelectedChauffeur] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedRemorque, setSelectedRemorque] = useState('');
  const [loadingMission, setLoadingMission] = useState(true);
  const [loadingResources, setLoadingResources] = useState(true);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [assignedMissions, setAssignedMissions] = useState([]);
  const [assignment, setAssignment] = useState(null);
  const [assignmentHistory, setAssignmentHistory] = useState(null);
  const [assignmentWarnings, setAssignmentWarnings] = useState([]);
  const [mobileData, setMobileData] = useState(INITIAL_MOBILE_DATA);
  const [loadingMobileData, setLoadingMobileData] = useState(false);
  const [mobileDataError, setMobileDataError] = useState(null);
  const [inspectionFilter, setInspectionFilter] = useState('latest');
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);

  // AI Suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [hasTriggeredSuggestions, setHasTriggeredSuggestions] = useState(false);

  // Live driver position for ETA
  const [driverPosition, setDriverPosition] = useState(null);

  // For EN_COURS:  driver position → delivery  (remaining live distance)
  // For EN_ATTENTE: driver position → pickup → delivery  (full trip estimate including drive to pickup)
  const { remainingMinutes, estimatedArrival, distanceKm: etaDistanceKm, loading: etaLoading } = useEtaCalculator({
    driverLat: driverPosition?.latitude ?? null,
    driverLng: driverPosition?.longitude ?? null,
    pickupLat: mission?.departureLat ?? null,
    pickupLng: mission?.departureLng ?? null,
    destLat: mission?.arrivalLat ?? null,
    destLng: mission?.arrivalLng ?? null,
    missionStarted: mission?.status === 'EN_COURS',
    driverSpeed: driverPosition?.speed ?? null,
    vehicleType: mission?.vehicleType ?? null,
    enabled: !!(mission?.arrivalLat && mission?.arrivalLng && (mission?.status === 'EN_COURS' || mission?.status === 'EN_ATTENTE')),
  });

  // Helper to get resource names from IDs
  const getChauffeurName = (id) => {
    if (!id || id === 'undefined' || id === 'null') return t('common.unknownChauffeur') || 'Chauffeur';
    const chauffeur = chauffeurs.find(c => String(c.id) === String(id));
    return chauffeur ? `${chauffeur.firstName} ${chauffeur.lastName}` : `Chauffeur #${id}`;
  };

  const getVehiclePlate = (id) => {
    const vehicle = vehicles.find(v => String(v.id) === String(id));
    return vehicle ? vehicle.licensePlate : `Véhicule #${id}`;
  };

  const getRemorqueName = (id) => {
    if (!id) return null;
    const remorque = remorques.find(r => String(r.id) === String(id));
    return remorque ? remorque.type : `Remorque #${id}`;
  };

  const formatDateTime = (value) => {
    if (!value) {
      return t('common.notSpecified');
    }
    try {
      return new Date(value).toLocaleString();
    } catch (err) {
      return value;
    }
  };

  const formatChauffeurOption = (chauffeur) => {
    if (!chauffeur) return '';
    const name = `${chauffeur.firstname || ''} ${chauffeur.lastname || ''}`.trim();
    if (chauffeur.hasActiveAssignment && chauffeur.activeMissionId !== missionId) {
      return `${name} • ${t('assignMission.resources.onMission')}`;
    }
    return name;
  };

  const formatVehicleOption = (vehicle) => {
    if (!vehicle) return '';
    const label = `${vehicle.brand || ''} ${vehicle.model || ''} - ${vehicle.plateNumber || ''}`.trim();
    if (vehicle.hasActiveAssignment) {
      return `${label} • ${t('assignMission.resources.onMission')}`;
    }
    return label;
  };

  const formatRemorqueOption = (remorque) => {
    if (!remorque) return '';
    const label = `${remorque.brand || ''} ${remorque.model || ''} - ${remorque.plateNumber || ''}`.trim();
    if (remorque.hasActiveAssignment) {
      return `${label} • ${t('assignMission.resources.onMission')}`;
    }
    return label;
  };

  const formatCurrency = (value, currency = 'MAD') => {
    if (value == null) {
      return '—';
    }
    const numericValue = typeof value === 'string' ? Number(value) : value;
    if (Number.isNaN(numericValue)) {
      return `${value} ${currency}`;
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency
      }).format(numericValue);
    } catch (error) {
      const rounded = typeof numericValue === 'number' ? numericValue.toFixed(2) : numericValue;
      return `${rounded} ${currency}`;
    }
  };

  const renderConflictSummary = (conflict) => {
    if (!conflict) return '';
    const resourceLabel = conflict.resourceType === 'CHAUFFEUR'
      ? t('common.chauffeurs')
      : t('common.vehicules');
    const missionRef = conflict.conflictingMissionReference || conflict.conflictingMissionId || t('common.mission');
    const windowLabel = `${formatDateTime(conflict.conflictingMissionPlannedStartAt)} → ${formatDateTime(conflict.conflictingMissionPlannedEndAt)}`;
    return t('assignMission.warnings.conflict', {
      resource: resourceLabel,
      resourceName: conflict.resourceName || conflict.resourceId,
      mission: missionRef,
      window: windowLabel
    });
  };
  const [finishing, setFinishing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);

  // Handle pre-selected chauffeur from Agent Control Center
  useEffect(() => {
    if (location.state?.selectedChauffeurId) {
      console.log('Pre-selecting chauffeur from navigation state:', location.state.selectedChauffeurId);
      setSelectedChauffeur(String(location.state.selectedChauffeurId));
      setIsReassigning(true);
    }
  }, [location.state]);

  // Load assigned missions when no missionId is provided
  useEffect(() => {
    const loadAssignedMissions = async () => {
      if (missionId) return; // Skip if we have a missionId

      try {
        setLoadingAssigned(true);
        setError(null);
        const data = await getAssignedMissions(selectedTransporteurId);
        setAssignedMissions(data);
      } catch (err) {
        setError(err.message || t('assignMission.errors.loadAssignedFailed'));
      } finally {
        setLoadingAssigned(false);
      }
    };

    loadAssignedMissions();
  }, [missionId, t, selectedTransporteurId]);

  // Load mission details when missionId is provided
  const loadMission = async () => {
    if (!missionId) {
      setLoadingMission(false);
      return;
    }

    try {
      const missionData = await getMission(missionId);
      if (!missionData) {
        setError(t('assignMission.errors.missionNotFound'));
        return;
      }
      setMission(missionData);
    } catch (err) {
      setError(err.message || t('assignMission.errors.loadMissionFailed'));
    } finally {
      setLoadingMission(false);
    }
  };

  useEffect(() => {
    loadMission();
  }, [missionId, t]);

  const [validatingStep, setValidatingStep] = useState(null);
  const [showDriverBusyModal, setShowDriverBusyModal] = useState(false);

  const handleValidateStep = async (stepKey) => {
    try {
      if (stepKey === 'start') {
        if (assignment) {
          const chauffeurId = assignment.chauffeurId || assignment.chauffeur_id;
          if (chauffeurId) {
            setValidatingStep(stepKey);
            // Single optimised backend call — one DB query, no N+1
            const isBusy = await isChauffeurActiveOnAnotherMission(chauffeurId, String(mission.id));
            if (isBusy) {
              setValidatingStep(null);
              setShowDriverBusyModal(true);
              return;
            }
          }
        }
      }

      setValidatingStep(stepKey);
      if (stepKey === 'start') {
        await updateMissionStatus(mission.id, 'EN_COURS');
        toast.success(t('assignMission.messages.successStart') || 'Mission démarrée avec succès');
      } else if (stepKey === 'finish') {
        await api.put(`/transporteur/missions/${mission.id}/finish`);
        toast.success(t('assignMission.messages.successFinish') || 'Mission terminée avec succès');
      } else {
        const timeFieldMap = {
          'arrivalLoading': 'loadingArrivalTime',
          'departureLoading': 'loadingDepartureTime',
          'arrivalDelivery': 'deliveryArrivalTime'
        };
        const fieldName = timeFieldMap[stepKey];
        if (fieldName) {
          const updatedMission = { ...mission, [fieldName]: new Date().toISOString() };
          await updateMission(mission.id, updatedMission);
          toast.success(t('assignMission.messages.successUpdate') || 'Étape validée avec succès');
        }
      }
      // Refresh mission
      await loadMission();
    } catch (err) {
      toast.error(err.message || t('common.error'));
    } finally {
      setValidatingStep(null);
    }
  };

  // Load assignment info for the mission
  useEffect(() => {
    const loadAssignment = async () => {
      if (!missionId) return;
      try {
        const assignmentData = await getMissionAssignment(missionId);
        setAssignment(assignmentData);
      } catch (err) {
        // If 404, no assignment exists yet
        setAssignment(null);
      }
    };
    loadAssignment();
  }, [missionId, success]);

  // Fetch the assigned driver's live GPS position for ETA calculation
  useEffect(() => {
    if (!assignment?.chauffeurId && !assignment?.chauffeur_id) return;
    const chauffeurId = assignment.chauffeurId || assignment.chauffeur_id;
    const fetchPosition = async () => {
      try {
        const positions = await getChauffeursPositions();
        const pos = positions.find(p => String(p.id) === String(chauffeurId) || String(p.chauffeurId) === String(chauffeurId));
        if (pos) setDriverPosition(pos);
      } catch (_) { /* silently ignore */ }
    };
    fetchPosition();
  }, [assignment]);

  // Load assignment history for finished/cancelled missions
  useEffect(() => {
    const loadHistory = async () => {
      if (!missionId || !mission) return;
      if (mission.status !== 'TERMINEE' && mission.status !== 'ANNULEE') return;
      try {
        const historyData = await getMissionAssignmentHistory(missionId);
        setAssignmentHistory(historyData);
      } catch (err) {
        setAssignmentHistory(null);
      }
    };
    loadHistory();
  }, [missionId, mission]);

  // Pre-fill existing vehicle/remorque when reassigning
  useEffect(() => {
    if (isReassigning && assignment) {
      if (!selectedVehicle && assignment.vehiculeId) {
        setSelectedVehicle(String(assignment.vehiculeId));
      }
      if (!selectedRemorque && assignment.remorqueId) {
        setSelectedRemorque(String(assignment.remorqueId));
      }
    }
  }, [isReassigning, assignment, selectedVehicle, selectedRemorque]);

  useEffect(() => {
    const loadMobileData = async () => {
      if (!missionId) {
        setMobileData(INITIAL_MOBILE_DATA);
        return;
      }
      setLoadingMobileData(true);
      setMobileDataError(null);
      try {
        const data = await getMissionMobileData(missionId);
        setMobileData({
          documents: data?.documents || [],
          incidents: data?.incidents || [],
          fuelDeclarations: data?.fuelDeclarations || [],
          vehicleInspections: data?.vehicleInspections || []
        });
      } catch (err) {
        setMobileData(INITIAL_MOBILE_DATA);
        setMobileDataError(err.message || t('assignMission.mobileData.errorFallback'));
      } finally {
        setLoadingMobileData(false);
      }
    };
    loadMobileData();
  }, [missionId, success, t]);

  const loadMobileDataNow = async () => {
      try {
        const data = await getMissionMobileData(missionId);
        setMobileData({
          documents: data?.documents || [],
          incidents: data?.incidents || [],
          fuelDeclarations: data?.fuelDeclarations || [],
          vehicleInspections: data?.vehicleInspections || []
        });
      } catch (err) {
        // Handle error silently or show a toast
      }
  };

  const handleUploadMissionDoc = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingDoc(true);
      await uploadMissionDocument(missionId, file, {
        name: file.name,
        type: 'MISSION_DOCUMENT'
      });
      toast.success(t('common.success') || 'Document ajouté');
      loadMobileDataNow(); // Refresh documents instantly
    } catch (err) {
      toast.error(t('common.error') + ": " + err.message);
    } finally {
      setIsUploadingDoc(false);
      event.target.value = '';
    }
  };

  useEffect(() => {
    setAssignmentWarnings([]);
  }, [missionId]);

  // Load resources when mission is loaded and not assigned
  useEffect(() => {
    const loadResources = async () => {
      if (!mission || (assignment && !isReassigning)) return;
      try {
        const [chauffeursData, vehiclesData, remorquesData] = await Promise.all([
          getAvailableChauffeurs(selectedTransporteurId),
          getAvailableVehicles(selectedTransporteurId),
          getAvailableRemorques(selectedTransporteurId)
        ]);
        // Filter out deactivated drivers (only show active drivers)
        const activeChauffeurs = (chauffeursData || []).filter(chauffeur => chauffeur.active === true);
        setChauffeurs(activeChauffeurs);
        setVehicles(vehiclesData || []);
        setRemorques(remorquesData || []);
      } catch (err) {
        setError(err.message || t('assignMission.errors.loadResourcesFailed'));
      } finally {
        setLoadingResources(false);
      }
    };

    loadResources();
  }, [mission, assignment, t, selectedTransporteurId]);

  const loadAISuggestions = async () => {
    if (!missionId || hasTriggeredSuggestions) return;

    // Show modal immediately to indicate loading
    setShowSuggestionsModal(true);
    setSuggestionsLoading(true);

    try {
      // Step 1: Trigger suggestion calculation
      await triggerAISuggestion(missionId);
      setHasTriggeredSuggestions(true);

      // Give the agent a moment to compute/propagate (similar to ACC delay)
      await new Promise(resolve => setTimeout(resolve, 1200));

      // Step 2: Fetch proposals
      const data = await getAIProposals(missionId);
      const filtered = Array.isArray(data) ? data : [];
      setSuggestions(filtered);

      // If no suggestions found, we might want to show a message in the modal instead of closing it
      // so the user knows the AI looked but found nothing.
      if (filtered.length === 0) {
        // setShowSuggestionsModal(false); // Commented out to keep modal open with "no suggestions" msg
      }
    } catch (err) {
      console.error('AI Suggestion error:', err);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  useEffect(() => {
    if (missionId && mission && mission.status === 'EN_ATTENTE' && !hasTriggeredSuggestions) {
      loadAISuggestions();
    }
  }, [missionId, mission, hasTriggeredSuggestions]);

  const handleAssign = async () => {
    if (!selectedChauffeur || !selectedVehicle) {
      setError(t('assignMission.errors.selectBoth'));
      return;
    }

    try {
      setAssigning(true);
      setError(null);
      setAssignmentWarnings([]);
      const result = await assignMissionResources(missionId, selectedChauffeur, selectedVehicle, selectedRemorque);
      if (result?.assignment) {
        setAssignment(result.assignment);
      }
      const conflicts = result?.conflicts || [];
      setAssignmentWarnings(conflicts);
      if (conflicts.length > 0) {
        const warningMessage = t('assignMission.warnings.detected', { count: conflicts.length });
        const resolvedMessage = warningMessage === 'assignMission.warnings.detected'
          ? `Planning conflicts detected (${conflicts.length})`
          : warningMessage;
        toast.warn(resolvedMessage);
      }
      setSuccess(t('assignMission.success.assigned'));
      if (conflicts.length === 0) {
        setTimeout(() => navigate('/missions'), 2000);
      }
    } catch (err) {
      setError(err.message || t('assignMission.errors.assignFailed'));
    } finally {
      setAssigning(false);
    }
  };

  const handleFinishMission = async () => {
    if (finishing) return;
    setFinishing(true);
    try {
      await api.put(`/transporteur/missions/${missionId}/finish`);
      toast.success(t('assignMission.success.finished'));
      // Refresh mission and assignment to ensure UI is in sync after backend cleanup
      const refreshed = await getMission(missionId);
      setMission(refreshed);
      const refreshedAssignment = await getMissionAssignment(missionId);
      setAssignment(refreshedAssignment);
      navigate('/missions');
    } catch (err) {
      toast.error(t('assignMission.errors.finishFailed'));
    } finally {
      setFinishing(false);
    }
  };

  const handleCancelMission = async () => {
    setCancelling(true);
    try {
      await api.put(`/transporteur/missions/${mission.id}/cancel`);
      toast.success(t('assignMission.success.cancelled'));
      setMission({ ...mission, status: 'ANNULEE' });
      setTimeout(() => navigate('/missions'), 1500);
    } catch (err) {
      toast.error(t('assignMission.errors.cancelFailed'));
    } finally {
      setCancelling(false);
    }
  };

  const handleRunAnalysis = async () => {
    if (!missionId) return;
    setAnalysisLoading(true);
    setAnalysisResult('');
    setShowAnalysisModal(true);
    try {
      const data = await analyzeMission(missionId);
      setAnalysisResult(data?.analysis || data?.text || 'Aucune analyse disponible.');
    } catch (err) {
      setAnalysisResult(`Erreur : ${err.message}`);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleAcceptSuggestion = async (proposal) => {
    console.log('[DEBUG] Accepting suggestion:', proposal);
    try {
      // AI Suggestions from live agent might not have a database ID
      if (proposal.id) {
        await decideAIProposal(proposal.id, 'CONFIRM');
      } else {
        console.warn('[DEBUG] Suggestion has no ID, skipping decideAIProposal');
      }

      // Auto-fill the form - Support both snake_case and camelCase from AI agent
      const chauffeurId = proposal.chauffeurId || proposal.chauffeur_id;
      const vehiculeId = proposal.vehiculeId || proposal.vehicule_id;
      const remorqueId = proposal.remorqueId || proposal.remorque_id;

      console.log('[DEBUG] Resolved IDs:', { chauffeurId, vehiculeId, remorqueId });

      if (chauffeurId) {
        console.log('[DEBUG] Setting chauffeur:', chauffeurId);
        setSelectedChauffeur(String(chauffeurId));
      }
      if (vehiculeId) setSelectedVehicle(String(vehiculeId));
      if (remorqueId) setSelectedRemorque(String(remorqueId));

      setShowSuggestionsModal(false);
      toast.success(t('assignMission.ai.suggestionApplied') || 'Suggestion appliquée !');
    } catch (err) {
      console.error('[DEBUG] Error applying suggestion:', err);
      toast.error(err.message);
    }
  };

  const handleRejectSuggestion = async (proposalId) => {
    try {
      await decideAIProposal(proposalId, 'REJECT');
      setSuggestions(prev => prev.filter(p => p.id !== proposalId));
      if (suggestions.length <= 1) {
        setShowSuggestionsModal(false);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Render assigned missions list
  const renderAssignedMissions = () => {
    if (loadingAssigned) {
      return (
        <div className="text-center p-3">
          <Spinner animation="border" /> {t('assignMission.loading.assignedMissions')}
        </div>
      );
    }

    if (assignedMissions.length === 0) {
      return (
        <Alert variant="info">
          {t('assignMission.noAssignedMissions')}
        </Alert>
      );
    }

    return (
      <div className="table-responsive">
        <table className="c-table table-hover">
          <thead>
            <tr>
              <th>{t('assignMission.table.from')}</th>
              <th>{t('assignMission.table.to')}</th>
              <th>{t('assignMission.table.status')}</th>
              <th>{t('assignMission.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {assignedMissions.map((mission) => (
              <tr 
                key={mission.id}
                onClick={() => navigate(`/missions/assign?missionId=${mission.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <td data-label={t('assignMission.table.from')}>
                  {mission.expediteurNom || mission.destinataireNom ? (
                    <div className="d-flex flex-column">
                      <span className="fw-bold">{mission.expediteurNom || mission.departureLocation}</span>
                      <span className="text-muted small fw-light">{mission.departureLocation}</span>
                    </div>
                  ) : (
                    <>{mission.departureLocation}</>
                  )}
                </td>
                <td data-label={t('assignMission.table.to')}>
                  {mission.expediteurNom || mission.destinataireNom ? (
                    <div className="d-flex flex-column">
                      <span className="fw-bold">{mission.destinataireNom || mission.arrivalLocation}</span>
                      <span className="text-muted small fw-light">{mission.arrivalLocation}</span>
                    </div>
                  ) : (
                    <>{mission.arrivalLocation}</>
                  )}
                </td>
                <td data-label={t('assignMission.table.status')}>
                  <span className={`badge rounded-pill ${mission.status === 'EN_ATTENTE' ? 'bg-warning text-dark' :
                    mission.status === 'EN_COURS' ? 'bg-primary' :
                      mission.status === 'TERMINEE' ? 'bg-success' :
                        mission.status === 'ANNULEE' ? 'bg-danger' :
                          'bg-secondary'
                    }`}>
                    {t(`missions.status.${mission.status.toLowerCase()}`)}
                  </span>
                </td>
                <td data-label={t('assignMission.table.actions')}>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); navigate(`/missions/assign?missionId=${mission.id}`); }}
                  >
                    {t('assignMission.actions.viewDetails')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMissionHero = () => {
    if (!mission) return null;
    const statusConfig = STATUS_VARIANTS[mission.status] || {};

    const heroStat = (label, value) => (
      <div key={label} className="d-flex flex-column">
        <small className="text-uppercase text-muted fw-semibold">{label}</small>
        <span className="fs-6 fw-semibold text-dark">{value || '—'}</span>
      </div>
    );

    return (
      <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
        <div
          style={{
            height: 6,
            background: 'linear-gradient(90deg, #0d6efd, #6f42c1)'
          }}
        />
        <Card.Body className="p-4">
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
            <div className="flex-grow-1">
              <small className="text-uppercase text-muted fw-semibold">
                {t('assignMission.missionDetails.title')}
              </small>
              <div className="mb-1">
                {mission.expediteurNom || mission.destinataireNom ? (
                  <div className="d-flex flex-column">
                    <h2 className="fw-bold mb-0 text-dark">
                      {mission.expediteurNom || mission.departureLocation || '—'}
                      <span className="mx-2 text-muted">→</span>
                      {mission.destinataireNom || mission.arrivalLocation || '—'}
                    </h2>
                    <span className="text-muted fs-6 fw-light mt-1">
                      {mission.departureLocation || '—'} → {mission.arrivalLocation || '—'}
                    </span>
                  </div>
                ) : (
                  <h2 className="fw-bold mb-0 text-dark">
                    {mission.departureLocation || '—'}
                    <span className="mx-2 text-muted">→</span>
                    {mission.arrivalLocation || '—'}
                  </h2>
                )}
              </div>
              <div className="d-flex align-items-center flex-wrap gap-2 text-muted">
                <FaFileAlt className="me-2 text-primary" />
                <span className="fw-semibold text-dark">
                  {mission.referenceNumber || t('common.mission')}
                </span>
              </div>
            </div>
            <Badge bg={statusConfig.badge || 'secondary'} className="px-3 py-2">
              {t(`missions.status.${mission.status.toLowerCase()}`)}
            </Badge>
            <Button
              variant="outline-primary"
              className="rounded-pill px-3 shadow-sm d-flex align-items-center gap-2"
              onClick={handleRunAnalysis}
              disabled={analysisLoading}
            >
              <FaBrain /> {t('common.aiAnalyse') || 'Analyse IA'}
            </Button>
          </div>
          <hr className="my-4" />
          <div className="d-flex flex-wrap gap-4">
            {heroStat(
              t('assignMission.hero.planningWindow'),
              `${mission.plannedStartAt ? formatDateTime(mission.plannedStartAt) : '—'} • ${mission.plannedEndAt ? formatDateTime(mission.plannedEndAt) : '—'}`
            )}
            {heroStat(t('assignMission.hero.cargo'), mission.cargoType || t('common.cargo'))}
            {heroStat(t('assignMission.hero.route'), `${mission.weight || 0} t • ${mission.length || 0} m`)}
          </div>
        </Card.Body>
      </Card >
    );
  };

  const renderMissionSnapshot = () => {
    if (!mission) return null;
    const snapshotSections = [
      {
        title: t('assignMission.snapshot.schedule'),
        items: [
          { label: t('assignMission.missionDetails.departureDate'), value: mission.departureDate ? new Date(mission.departureDate).toLocaleString() : '—' },
          { label: t('assignMission.missionDetails.arrivalDate'), value: mission.arrivalDate ? new Date(mission.arrivalDate).toLocaleString() : '—' }
        ]
      },
      {
        title: t('assignMission.snapshot.freight'),
        items: [
          { label: t('assignMission.missionDetails.weight'), value: mission.weight ? `${mission.weight} t` : '—' },
          { label: t('assignMission.missionDetails.volume'), value: mission.volume ? `${mission.volume} m³` : '—' },
          { label: t('assignMission.missionDetails.length'), value: mission.length ? `${mission.length} m` : '—' }
        ]
      },
      {
        title: t('assignMission.snapshot.transport'),
        items: [
          { label: t('common.vehicle'), value: mission.vehicleType || '—' },
          { label: t('assignMission.missionDetails.dangerousMaterial'), value: mission.isDangerousMaterial ? t('common.yes') : t('common.no') }
        ]
      },
      {
        title: t('assignMission.snapshot.financials'),
        items: [
          { label: t('common.price'), value: mission.price ? formatCurrency(mission.price) : '—' }
        ]
      }
    ];
    return (
      <Card className="mb-4 shadow-sm border-0">
        <Card.Header className="bg-white py-3 px-4 border-0">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle me-3"
              style={{ width: 10, height: 10, backgroundColor: '#0d6efd' }}
            ></div>
            <h6 className="mb-0 fw-semibold text-uppercase text-muted">
              {t('assignMission.snapshot.title')}
            </h6>
          </div>
        </Card.Header>
        <Card.Body className="px-4 pb-4 pt-0">
          <div className="d-flex flex-column gap-3">
            {snapshotSections.map((section) => (
              <div key={section.title} className="p-3 rounded-3" style={{ backgroundColor: '#f8f9fa' }}>
                <small className="text-uppercase text-muted fw-semibold">{section.title}</small>
                <div className="mt-2 d-flex flex-column gap-1">
                  {section.items.map((item) => (
                    <div key={item.label} className="d-flex justify-content-between text-dark">
                      <span className="text-muted">{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Render detailed mission specs in a professional grid
  const renderMissionDetails = () => {
    if (!mission) return null;
    const detailFields = [
      { label: t('common.ref'), value: mission.referenceNumber },
      { label: t('common.description'), value: mission.description || '—' },
      { label: t('common.departure'), value: mission.departureLocation },
      { label: t('common.arrival'), value: mission.arrivalLocation },
      { label: t('assignMission.missionDetails.departureDate'), value: mission.departureDate ? new Date(mission.departureDate).toLocaleString() : '—' },
      { label: t('assignMission.missionDetails.arrivalDate'), value: mission.arrivalDate ? new Date(mission.arrivalDate).toLocaleString() : '—' }
      //{ label: 'Planned start', value: mission.plannedStartAt ? formatDateTime(mission.plannedStartAt) : '—' },
      //{ label: 'Planned end', value: mission.plannedEndAt ? formatDateTime(mission.plannedEndAt) : '—' }
    ];
    return (
      <Card className="mb-4 shadow-sm border-0">
        <Card.Header className="bg-white d-flex align-items-center py-3 px-4 border-0">
          <FaCar className="me-2 text-primary" />
          <span className="fw-semibold">{t('assignMission.missionDetails.title')}</span>
        </Card.Header>
        <Card.Body className="px-4 pb-4">
          <Row className="gy-3">
            {detailFields.map((field) => (
              <Col md={6} key={field.label}>
                <small className="text-uppercase text-muted fw-semibold">{field.label}</small>
                <div className="fw-semibold text-dark mt-1">{field.value || '—'}</div>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // Render assignment details in a modern card
  const renderAssignmentDetails = () => {
    if (!assignment) return null;
    let vehicleDisplay = assignment.vehiculeBrand && assignment.vehiculeModel && assignment.vehiculePlateNumber
      ? `${assignment.vehiculeBrand} ${assignment.vehiculeModel} - ${assignment.vehiculePlateNumber}`
      : assignment.vehiculePlateNumber
        ? assignment.vehiculePlateNumber
        : assignment.vehiculeId;
    return (
      <Card className="mb-4 border-0 shadow-sm rounded-4 overflow-hidden">
        <div style={{ height: 6, background: 'linear-gradient(90deg, #0d6efd, #198754)' }}></div>
        <Card.Body className="p-4">
          <div className="d-flex align-items-center mb-3">
            <FaCheckCircle className="me-2 text-success" />
            <h6 className="mb-0 text-uppercase text-muted fw-semibold">
              {t('assignMission.assignedResources.title')}
            </h6>
          </div>
          <div className="d-flex flex-column gap-3">
            <div className="d-flex align-items-start gap-3">
              <div className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                <FaUserTie />
              </div>
              <div>
                <small className="text-muted text-uppercase fw-semibold">{t('common.chauffeurs')}</small>
                <div className="fs-5 fw-semibold">{assignment.chauffeurName || assignment.chauffeurId}</div>
              </div>
            </div>
            <div className="d-flex align-items-start gap-3">
              <div className="rounded-circle bg-secondary bg-opacity-10 text-secondary d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                <FaCar />
              </div>
              <div>
                <small className="text-muted text-uppercase fw-semibold">{t('common.vehicules')}</small>
                <div className="fs-5 fw-semibold">{vehicleDisplay}</div>
              </div>
            </div>
            {assignment.remorquePlateNumber && (
              <div className="d-flex align-items-start gap-3">
                <div className="rounded-circle bg-info bg-opacity-10 text-info d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                  <FaTrailer />
                </div>
                <div>
                  <small className="text-muted text-uppercase fw-semibold">{t('remorques.title')}</small>
                  <div className="fs-5 fw-semibold">
                    {assignment.remorqueBrand && assignment.remorqueModel
                      ? `${assignment.remorqueBrand} ${assignment.remorqueModel} - ${assignment.remorquePlateNumber}`
                      : assignment.remorquePlateNumber}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card.Body>
        <Card.Footer className="bg-white border-0 d-flex justify-content-end gap-2 px-4 pb-4">
          {['EN_COURS', 'EN_ATTENTE'].includes(mission?.status) && (
            <Button
              variant="danger"
              onClick={handleCancelMission}
              disabled={cancelling}
              title={t('assignMission.actions.cancelTitle')}
            >
              {cancelling ? (
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              ) : (
                <i className="bi bi-x-circle me-1"></i>
              )}
              {t('assignMission.actions.cancel')}
            </Button>
          )}
          {mission?.status === 'EN_COURS' && (
            <Button
              variant="success"
              onClick={handleFinishMission}
              disabled={finishing}
              title={t('assignMission.actions.finishTitle')}
            >
              {finishing ? (
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              ) : (
                <i className="bi bi-check2-circle me-1"></i>
              )}
              {t('assignMission.actions.finish')}
            </Button>
          )}
        </Card.Footer>
      </Card>
    );
  };

  // Render archived assignment (read-only) for completed/cancelled missions
  const renderAssignmentArchive = () => {
    const source = assignmentHistory;
    if (!source) {
      return (
        <Card className="mb-4 border-0 shadow-sm rounded-4 overflow-hidden">
          <div style={{ height: 6, background: 'linear-gradient(90deg, #6c757d, #adb5bd)' }} />
          <Card.Body className="p-4">
            <div className="d-flex align-items-center mb-3">
              <FaCheckCircle className="me-2 text-secondary" />
              <h6 className="mb-0 text-uppercase text-muted fw-semibold">
                {t('assignMission.assignedResources.title')}
              </h6>
            </div>
            <div className="text-muted small fst-italic">
              {t('assignMission.history.noHistory') || 'Aucun historique d\'affectation disponible.'}
            </div>
          </Card.Body>
        </Card>
      );
    }

    const vehicleDisplay = source.vehiculeBrand && source.vehiculeModel && source.vehiculePlateNumber
      ? `${source.vehiculeBrand} ${source.vehiculeModel} - ${source.vehiculePlateNumber}`
      : source.vehiculePlateNumber || `Véhicule #${source.vehiculeId}`;

    return (
      <Card className="mb-4 border-0 shadow-sm rounded-4 overflow-hidden">
        <div style={{ height: 6, background: 'linear-gradient(90deg, #6c757d, #adb5bd)' }} />
        <Card.Body className="p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <FaCheckCircle className="me-2 text-secondary" />
              <h6 className="mb-0 text-uppercase text-muted fw-semibold">
                {t('assignMission.assignedResources.title')}
              </h6>
            </div>
            <Badge bg="secondary" className="rounded-pill px-3">
              {t('assignMission.history.archived') || 'Archivé'}
            </Badge>
          </div>

          <div className="d-flex flex-column gap-3">
            {/* Chauffeur */}
            <div className="d-flex align-items-start gap-3">
              <div className="rounded-circle bg-secondary bg-opacity-10 text-secondary d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                <FaUserTie />
              </div>
              <div>
                <small className="text-muted text-uppercase fw-semibold">{t('common.chauffeurs')}</small>
                <div className="fs-5 fw-semibold text-dark">{source.chauffeurName || `Chauffeur #${source.chauffeurId}`}</div>
              </div>
            </div>

            {/* Véhicule */}
            <div className="d-flex align-items-start gap-3">
              <div className="rounded-circle bg-secondary bg-opacity-10 text-secondary d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                <FaCar />
              </div>
              <div>
                <small className="text-muted text-uppercase fw-semibold">{t('common.vehicules')}</small>
                <div className="fs-5 fw-semibold text-dark">{vehicleDisplay}</div>
              </div>
            </div>

            {/* Remorque (optionnelle) */}
            {source.remorquePlateNumber && (
              <div className="d-flex align-items-start gap-3">
                <div className="rounded-circle bg-secondary bg-opacity-10 text-secondary d-flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                  <FaTrailer />
                </div>
                <div>
                  <small className="text-muted text-uppercase fw-semibold">{t('remorques.title')}</small>
                  <div className="fs-5 fw-semibold text-dark">
                    {source.remorqueBrand && source.remorqueModel
                      ? `${source.remorqueBrand} ${source.remorqueModel} - ${source.remorquePlateNumber}`
                      : source.remorquePlateNumber}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Dates archive */}
          {(source.assignedDate || source.completedDate) && (
            <div className="mt-4 pt-3 border-top d-flex flex-column gap-1">
              {source.assignedDate && (
                <small className="text-muted">
                  <strong>{t('assignMission.history.assignedOn') || 'Assigné le'} :</strong>{' '}
                  {new Date(source.assignedDate).toLocaleString()}
                </small>
              )}
              {source.completedDate && (
                <small className="text-muted">
                  <strong>{t('assignMission.history.completedOn') || 'Terminé le'} :</strong>{' '}
                  {new Date(source.completedDate).toLocaleString()}
                </small>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Render assignment form in a modern card
  const renderAssignmentForm = () => (
    <Card className="mb-4 border-0 shadow-sm rounded-4 overflow-hidden">
      <div style={{ height: 6, background: 'linear-gradient(90deg, #0d6efd, #6f42c1)' }}></div>
      <Card.Body className="p-4">
        <div className="d-flex align-items-center mb-3">
          <FaUserTie className="me-2 text-primary" />
          <h6 className="mb-0 text-uppercase text-muted fw-semibold">
            {t('assignMission.assignResources.title')}
          </h6>
        </div>
        <Form>
          <Form.Group className="mb-3">
            {/* Labels removed due to border overlap issue */}
            {loadingResources ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Form.Select
                value={selectedChauffeur}
                onChange={(e) => setSelectedChauffeur(e.target.value)}
                disabled={assigning}
              >
                <option value="">{t('assignMission.assignResources.chooseChauffeur')}</option>
                {chauffeurs.map(chauffeur => (
                  <option key={chauffeur.id} value={chauffeur.id}>
                    {formatChauffeurOption(chauffeur)}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            {/* Labels removed due to border overlap issue */}
            {loadingResources ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Form.Select
                value={selectedVehicle}
                onChange={(e) => setSelectedVehicle(e.target.value)}
                disabled={assigning}
              >
                <option value="">{t('assignMission.assignResources.chooseVehicle')}</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {formatVehicleOption(vehicle)}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            {/* Label removed due to border overlap issue as requested by user */}
            {loadingResources ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Form.Select
                value={selectedRemorque}
                onChange={(e) => setSelectedRemorque(e.target.value)}
                disabled={assigning}
              >
                <option value="">{t('assignMission.assignResources.chooseRemorque')}</option>
                {remorques.map(remorque => (
                  <option key={remorque.id} value={remorque.id}>
                    {formatRemorqueOption(remorque)}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={assigning || loadingResources || !selectedChauffeur || !selectedVehicle}
            className="w-100"
          >
            {assigning ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('assignMission.assignResources.assigning')}
              </>
            ) : (
              t('assignMission.assignResources.assignButton')
            )}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderSectionLoading = () => (
    <div className="text-center py-3">
      <Spinner animation="border" size="sm" className="me-2" />
      {t('assignMission.mobileData.loading')}
    </div>
  );

  const renderMissionDocumentsSection = () => {
    const documents = mobileData?.documents || [];
    return (
      <Card className="shadow-sm h-100 border-0 rounded-4">
        <Card.Header className="bg-white d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FaFileAlt className="me-2 text-primary" />
            <span>{t('assignMission.missionDocuments.title')}</span>
            <Badge bg="secondary" className="ms-2">{documents.length}</Badge>
          </div>
          <div>
            <input
              type="file"
              id="assign-mission-doc-upload"
              className="d-none"
              onChange={handleUploadMissionDoc}
              disabled={isUploadingDoc}
            />
            <Button
              as="label"
              htmlFor="assign-mission-doc-upload"
              variant="outline-primary"
              size="sm"
              className="rounded-pill px-3 mb-0 pointer"
              disabled={isUploadingDoc}
            >
              {isUploadingDoc ? <Spinner animation="border" size="sm" /> : <><FaUpload className="me-1" /> {t('common.upload') || 'Ajouter'}</>}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {loadingMobileData ? (
            renderSectionLoading()
          ) : documents.length === 0 ? (
            <div className="text-muted">{t('assignMission.missionDocuments.empty')}</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {documents.map((doc) => (
                <a
                  key={doc.id}
                   href={getFileUrl(doc.fileUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="p-3 rounded border text-decoration-none"
                >
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                      <div className="fw-semibold text-dark">
                        <i className="bi bi-file-earmark-text me-2"></i>
                        {doc.name || t('assignMission.missionDocuments.unnamed')}
                      </div>
                      <small className="text-muted">{doc.createdAt ? formatDateTime(doc.createdAt) : '—'}</small>
                    </div>
                    <Badge bg="light" text="dark">
                      {doc.type || t('assignMission.missionDocuments.defaultType')}
                    </Badge>
                  </div>
                </a>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderMissionIncidentsSection = () => {
    const incidents = mobileData?.incidents || [];
    return (
      <Card className="shadow-sm h-100 border-0 rounded-4">
        <Card.Header className="bg-white d-flex align-items-center">
          <FaExclamationTriangle className="me-2 text-warning" />
          <span>{t('assignMission.incidents.title')}</span>
          <Badge bg="secondary" className="ms-auto">{incidents.length}</Badge>
        </Card.Header>
        <Card.Body>
          {loadingMobileData ? (
            renderSectionLoading()
          ) : incidents.length === 0 ? (
            <div className="text-muted">{t('assignMission.incidents.empty')}</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {incidents.map((incident) => (
                <div key={incident.id} className="p-3 border rounded">
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                    <Badge bg="warning" text="dark">
                      {incident.category || t('assignMission.incidents.unknownCategory')}
                    </Badge>
                    <small className="text-muted d-flex align-items-center">
                      <i className="bi bi-clock me-1"></i>
                      {formatDateTime(incident.createdAt)}
                    </small>
                  </div>
                  <p className="mb-2 text-dark">
                    {incident.description || t('assignMission.incidents.noDescription')}
                  </p>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <small className="text-muted">
                      {incident.chauffeurName || t('assignMission.incidents.unknownChauffeur')}
                    </small>
                    {incident.photoUrl && (
                      <a
                        href={getFileUrl(incident.photoUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                      >
                        {t('assignMission.incidents.viewPhoto')}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderFuelDeclarationsSection = () => {
    const fuelDeclarations = mobileData?.fuelDeclarations || [];
    return (
      <Card className="shadow-sm h-100 border-0 rounded-4">
        <Card.Header className="bg-white d-flex align-items-center">
          <FaGasPump className="me-2 text-success" />
          <span>{t('assignMission.fuel.title')}</span>
          <Badge bg="secondary" className="ms-auto">{fuelDeclarations.length}</Badge>
        </Card.Header>
        <Card.Body>
          {loadingMobileData ? (
            renderSectionLoading()
          ) : fuelDeclarations.length === 0 ? (
            <div className="text-muted">{t('assignMission.fuel.empty')}</div>
          ) : (
            <div className="table-responsive">
              <Table size="sm" hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>{t('assignMission.fuel.headers.date')}</th>
                    <th>{t('assignMission.fuel.headers.odometer')}</th>
                    <th>{t('assignMission.fuel.headers.liters')}</th>
                    <th>{t('assignMission.fuel.headers.amount')}</th>
                    <th>{t('assignMission.fuel.headers.ticket')}</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelDeclarations.map((declaration) => (
                    <tr key={declaration.id}>
                      <td>{formatDateTime(declaration.declaredAt)}</td>
                      <td>{declaration.odometerKm ? `${declaration.odometerKm} km` : '—'}</td>
                      <td>{declaration.litersAdded ? `${declaration.litersAdded} L` : '—'}</td>
                      <td>{formatCurrency(declaration.amountPaid, declaration.currency)}</td>
                      <td>
                        {declaration.ticketUrl ? (
                          <Button
                            as="a"
                            href={declaration.ticketUrl}
                            target="_blank"
                            rel="noreferrer"
                            size="sm"
                            variant="outline-secondary"
                          >
                            {t('assignMission.fuel.viewTicket')}
                          </Button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderInspectionBadge = (label, value) => {
    const variant = value === true ? 'success' : value === false ? 'danger' : 'secondary';
    const text = value === true
      ? t('common.yes')
      : value === false
        ? t('common.no')
        : t('assignMission.vehicleInspections.notProvided');
    return (
      <Badge bg={variant} className="me-2 mb-2">
        {label}: {text}
      </Badge>
    );
  };

  const renderVehicleInspectionsSection = () => {
    const inspections = mobileData?.vehicleInspections || [];
    const filteredInspections = inspectionFilter === 'latest'
      ? inspections.slice(0, 1)
      : inspections;
    return (
      <Card className="shadow-sm h-100 border-0 rounded-4">
        <Card.Header className="bg-white d-flex align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <FaClipboardCheck className="me-2 text-info" />
            <span>{t('assignMission.vehicleInspections.title')}</span>
          </div>
          <Badge bg="secondary">{inspections.length}</Badge>
          <div className="ms-auto btn-group btn-group-sm">
            <Button
              variant={inspectionFilter === 'latest' ? 'primary' : 'outline-secondary'}
              onClick={() => setInspectionFilter('latest')}
            >
              {t('assignMission.vehicleInspections.filter.latest')}
            </Button>
            <Button
              variant={inspectionFilter === 'all' ? 'primary' : 'outline-secondary'}
              onClick={() => setInspectionFilter('all')}
            >
              {t('assignMission.vehicleInspections.filter.all')}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {loadingMobileData ? (
            renderSectionLoading()
          ) : filteredInspections.length === 0 ? (
            <div className="text-muted">{t('assignMission.vehicleInspections.empty')}</div>
          ) : (
            <>
              {inspectionFilter === 'latest' && inspections.length > 1 && (
                <small className="text-muted d-block mb-3">
                  {t('assignMission.vehicleInspections.showingLatest', { count: inspections.length })}
                </small>
              )}
              {filteredInspections.map((inspection) => (
                <div key={inspection.id} className="p-3 border rounded mb-3">
                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <strong>{formatDateTime(inspection.inspectedAt)}</strong>
                    {inspection.odometer && (
                      <small className="text-muted">
                        {t('assignMission.vehicleInspections.odometer', { value: inspection.odometer })}
                      </small>
                    )}
                  </div>
                  <div className="mt-3 d-flex flex-wrap">
                    {renderInspectionBadge(t('assignMission.vehicleInspections.documentsOk'), inspection.documentsOk)}
                    {renderInspectionBadge(t('assignMission.vehicleInspections.fuelOk'), inspection.fuelOk)}
                    {renderInspectionBadge(t('assignMission.vehicleInspections.lightsOk'), inspection.lightsOk)}
                    {renderInspectionBadge(t('assignMission.vehicleInspections.wheelsOk'), inspection.wheelsOk)}
                    {inspection.fuelLevel && (
                      <Badge bg="light" text="dark" className="me-2 mb-2">
                        {t('assignMission.vehicleInspections.fuelLevel', { value: inspection.fuelLevel })}
                      </Badge>
                    )}
                  </div>
                  {inspection.notes && (
                    <p className="mt-3 mb-0 text-muted">{inspection.notes}</p>
                  )}
                  {inspection.photos && inspection.photos.length > 0 && (
                    <div className="mt-3 d-flex flex-wrap gap-2">
                      {inspection.photos.map((photo) => (
                        <a
                          key={photo.id}
                          href={getFileUrl(photo.fileUrl)}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-outline-secondary"
                        >
                          {photo.category || t('assignMission.vehicleInspections.photo')}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderMobileDataGrid = () => (
    <>
      <Row className="g-4">
        <Col md={6}>{renderMissionDocumentsSection()}</Col>
        <Col md={6}>{renderFuelDeclarationsSection()}</Col>
      </Row>
      <Row className="g-4 mt-1">
        <Col md={6}>{renderMissionIncidentsSection()}</Col>
        <Col md={6}>{renderVehicleInspectionsSection()}</Col>
      </Row>
    </>
  );

  // If no missionId is provided, show the list of assigned missions
  if (!missionId) {
    return (
      <DashboardLayout>
        <div className="container-fluid">
          <PageHeader
            title={t('assignMission.assignedMissions.title')}
            subtitle={t('missions.missionList.subtitle') || "Liste des missions en cours et assignées"}
            buttonText={t('common.back')}
            buttonTo="/missions"
          />
          <div className="c-table-card card">
            <div className="card-body">
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              {assignmentWarnings.length > 0 && (
                <Alert variant="warning">
                  <strong>{t('assignMission.warnings.detectedTitle')}</strong>
                  <ul className="mb-0 ps-3">
                    {assignmentWarnings.map((conflict, index) => (
                      <li key={`${conflict.resourceType}-${conflict.resourceId}-${index}`}>
                        {renderConflictSummary(conflict)}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
              {renderAssignedMissions()}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // If missionId is provided, show the mission details and assignment info
  return (
    <DashboardLayout>
      <div className="container-fluid px-0 px-md-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <p className="text-uppercase text-muted fw-semibold mb-1 small">
              {t('common.mission')}
            </p>
            <h3 className="fw-bold mb-0">{t('assignMission.missionDetails.title')}</h3>
          </div>
          <div className="d-flex gap-2">
            {missionId && (
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/missions/edit/${missionId}`)}
                className="d-flex align-items-center"
              >
                <FaPencilAlt className="me-2" />
                {t('common.edit')}
              </Button>
            )}
            <Button variant="outline-primary" onClick={() => navigate('/missions')}>
              <FaArrowLeft className="me-1" />{t('common.back')}
            </Button>
          </div>
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {loadingMission ? (
          <div className="text-center p-3">
            <Spinner animation="border" /> {t('assignMission.loading.missionDetails')}
          </div>
        ) : mission ? (
          <>
            {renderMissionHero()}
            {/* ── Mission Timeline ── */}
            <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
              <Card.Body className="p-4">
                <h6 className="text-uppercase text-muted fw-bold small mb-3 px-2 d-flex justify-content-between align-items-center">
                  <span>{t('missions.status.timeline.title') || 'Suivi de progression'}</span>
                </h6>

                {/* Live ETA bar — simple, minimalist design */}
                <div className="d-flex rounded-3 border mb-4 bg-white" style={{ borderColor: '#e2e8f0' }}>

                  {/* Remaining time */}
                  <div className="flex-fill text-center py-3 px-2">
                    <div className="text-muted text-uppercase mb-1" style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.05em' }}>
                      {t('assignMission.eta.remainingTime') || 'Temps restant'}
                    </div>
                    {etaLoading ? (
                      <Spinner animation="border" size="sm" variant="secondary" />
                    ) : (
                      <div className="text-dark fw-bold" style={{ fontSize: '1.4rem' }}>
                        {remainingMinutes !== null
                          ? remainingMinutes >= 60
                            ? `${Math.floor(remainingMinutes / 60)}h ${remainingMinutes % 60}min`
                            : `${remainingMinutes} min`
                          : '--'}
                      </div>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="border-start" style={{ width: 1, borderColor: '#e2e8f0' }} />

                  {/* Estimated arrival */}
                  <div className="flex-fill text-center py-3 px-2">
                    <div className="text-muted text-uppercase mb-1" style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.05em' }}>
                      {t('assignMission.eta.estimatedArrival') || 'Arrivée estimée'}
                    </div>
                    <div className="text-dark fw-bold" style={{ fontSize: '1.4rem' }}>
                      {estimatedArrival || '--:--'}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-start" style={{ width: 1, borderColor: '#e2e8f0' }} />

                  {/* Distance */}
                  <div className="flex-fill text-center py-3 px-2">
                    <div className="text-muted text-uppercase mb-1" style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.05em' }}>
                      {t('assignMission.eta.distanceKm') || 'Dist. (km)'}
                    </div>
                    <div className="text-dark fw-bold" style={{ fontSize: '1.4rem' }}>
                      {etaDistanceKm !== null ? etaDistanceKm : '--'}
                    </div>
                  </div>
                </div>

                {!driverPosition && assignment && (
                  <p className="text-muted small text-center mb-3">
                    <FaClock className="me-1" />
                    {t('assignMission.eta.noPosition') || 'Position du chauffeur non disponible — ETA non calculable'}
                  </p>
                )}
                {!assignment && (
                  <p className="text-muted small text-center mb-3">
                    <FaClock className="me-1" />
                    {t('assignMission.eta.noAssignment') || 'Aucun chauffeur assigné — ETA non disponible'}
                  </p>
                )}
                <div className="timeline-steps">
                  {[
                    { key: 'start', label: t('missions.status.timeline.departure') || 'Départ', date: mission.departureDate, icon: <FaRoute />, done: mission.status === 'EN_COURS' || mission.status === 'TERMINEE' || mission.status === 'ANNULEE' },
                    { key: 'arrivalLoading', label: t('missions.status.timeline.arrivalLoading') || 'Arrivée chargement', date: mission.loadingArrivalTime, icon: <FaCheckCircle />, done: !!mission.loadingArrivalTime },
                    { key: 'departureLoading', label: t('missions.status.timeline.departureLoading') || 'Départ chargement', date: mission.loadingDepartureTime, icon: <FaCheckCircle />, done: !!mission.loadingDepartureTime },
                    { key: 'arrivalDelivery', label: t('missions.status.timeline.arrivalDelivery') || 'Arrivée livraison', date: mission.deliveryArrivalTime, icon: <FaCheckCircle />, done: !!mission.deliveryArrivalTime },
                    { key: 'finish', label: t('missions.status.timeline.completed') || 'Terminée', date: (mission.status === 'TERMINEE' || mission.status === 'ANNULEE') ? mission.updatedAt : null, icon: <FaCheckCircle />, done: mission.status === 'TERMINEE' || mission.status === 'ANNULEE' }
                  ].map((step, idx, arr) => {
                    const isNext = !step.done && (idx === 0 || arr[idx - 1].done);
                    return (
                      <React.Fragment key={idx}>
                        <div className={`timeline-step ${step.done ? 'done' : ''} ${isNext ? 'active' : ''}`}>
                          {isNext ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`tooltip-${idx}`}>{t('common.validate') || 'Valider cette étape'}</Tooltip>}
                            >
                              <div 
                                className="marker" 
                                onClick={() => {
                                  if (!assignment && step.key === 'start') {
                                    toast.error(t('assignMission.errors.assignDriverFirst') || "Assignez un chauffeur d'abord");
                                    return;
                                  }
                                  if (validatingStep !== step.key) {
                                    handleValidateStep(step.key);
                                  }
                                }}
                              >
                                {validatingStep === step.key ? <Spinner size="sm" animation="border" variant="primary" /> : step.icon}
                              </div>
                            </OverlayTrigger>
                          ) : (
                            <div className="marker">{step.icon}</div>
                          )}
                          <div className="step-info">
                            <span className="step-label">{step.label}</span>
                            <span className="step-time">
                              {step.date ? new Date(step.date).toLocaleString() : '—'}
                            </span>
                            {isNext && (
                              <div className="validate-floating-badge">
                                {t('common.validate') || 'Valider'}
                              </div>
                            )}
                          </div>
                          {idx < arr.length - 1 && <div className="timeline-connector" />}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
            <Row className="g-4">
              <Col lg={4}>
                {/* For finished/cancelled missions, always show the archive (history). For active missions, show live assignment or the form. */}
                {(mission?.status === 'TERMINEE' || mission?.status === 'ANNULEE')
                  ? renderAssignmentArchive()
                  : (assignment && !isReassigning) ? renderAssignmentDetails() : renderAssignmentForm()}
                {renderMissionSnapshot()}
              </Col>
              <Col lg={8}>
                {renderMissionDetails()}
                {mobileDataError && (
                  <Alert variant="warning">{mobileDataError}</Alert>
                )}
                <div className="mt-4 mb-3 d-flex align-items-center justify-content-between">
                  <h5 className="fw-semibold mb-0">{t('assignMission.mobileData.title')}</h5>
                </div>
                {renderMobileDataGrid()}
              </Col>
            </Row>
          </>
        ) : (
          <Alert variant="danger">{t('assignMission.errors.missionNotFound')}</Alert>
        )}
      </div>
      {/* Premium AI Suggestions Modal */}
      <Modal
        show={showSuggestionsModal}
        onHide={() => setShowSuggestionsModal(false)}
        size="lg"
        centered
        className="acc-modal-premium"
      >
        <Modal.Body className="p-0 overflow-hidden rounded-4 border-0">
          <div className="bg-white">
            <div className="p-4 border-bottom d-flex justify-content-between align-items-center bg-light bg-opacity-50">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary text-white p-3 rounded-4 shadow-sm">
                  <FaRobot size={24} />
                </div>
                <div>
                  <h4 className="fw-bold mb-0 text-dark">{t('assignMission.ai.suggestionsTitle') || 'Optimisation IA'}</h4>
                  <p className="text-muted small mb-0">{t('assignMission.ai.suggestionsSub') || 'Suggestions d\'affectation intelligentes basées sur votre flotte active.'}</p>
                </div>
              </div>
              <Button variant="light" className="rounded-circle border-0" onClick={() => setShowSuggestionsModal(false)}>
                <FaTimes />
              </Button>
            </div>

            <div className="p-4" style={{ minHeight: '18.75rem', maxHeight: '70vh', overflowY: 'auto' }}>{/* 300px / 16 = 18.75rem */}
              {suggestionsLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <p className="text-muted">{t('common.aiAnalyseLoading') || 'Génération des suggestions...'}</p>
                </div>
              ) : suggestions.length === 0 ? (
                <div className="text-center py-5">
                  <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
                    <FaRobot size={32} className="text-muted opacity-50" />
                  </div>
                  <h5 className="text-dark fw-bold">Aucune suggestion trouvée</h5>
                  <p className="text-muted small">L'IA n'a pas pu identifier d'affectation optimale pour le moment.</p>
                </div>
              ) : (
                <div className="row g-4">
                  {suggestions.map((p, i) => {
                    const confidence = p.confidenceScore || (p.score_global ? Math.round(p.score_global * 100) : null);
                    const driverName = p.chauffeur_nom || p.full_name || p.fullName || getChauffeurName(p.chauffeur_id || p.chauffeurId);
                    const phoneNumber = p.phone_number || p.phoneNumber;
                    const vehicleId = p.vehicule_id || p.vehiculeId;
                    const vehiclePlate = p.plate_number || p.plateNumber || (vehicleId && vehicleId !== 'undefined' ? getVehiclePlate(vehicleId) : null);
                    const remorqueId = p.remorque_id || p.remorqueId;
                    const remorqueName = p.remorque_nom || p.remorqueName || (remorqueId && remorqueId !== 'undefined' ? getRemorqueName(remorqueId) : null);

                    return (
                      <div key={p.id || i} className="col-lg-6">
                        <div className="h-100 border rounded-4 p-4 shadow-hover transition-all bg-white position-relative overflow-hidden group">
                          {/* Confidence indicator side bar */}
                          <div className="position-absolute start-0 top-0 bottom-0 bg-primary opacity-10" style={{ width: '0.25rem' }}></div>{/* 4px / 16 = 0.25rem */}

                          <div className="d-flex justify-content-between align-items-start mb-4">
                            <div className="d-flex align-items-center gap-3">
                              <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-3">
                                <FaUserTie />
                              </div>
                              <div>
                                <div className="fw-bold fs-5 text-dark">{driverName}</div>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="badge bg-light text-muted fw-normal">{p.exploitant_nom || 'Interne'}</div>
                                  {phoneNumber && (
                                    <div className="text-primary small d-flex align-items-center gap-1">
                                      <FaPhone size={10} />
                                      <span>{phoneNumber}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {confidence !== null && (
                              <div className="text-end">
                                <div className="h3 fw-bold text-primary mb-0">{confidence}%</div>
                                <div className="text-uppercase fw-bold text-muted" style={{ fontSize: '0.6rem', letterSpacing: '0.05rem' }}>Confiance</div>
                              </div>
                            )}
                          </div>

                          <div className="space-y-3 mb-4">
                            {(vehiclePlate && !String(vehiclePlate).includes('undefined')) && (
                              <div className="d-flex align-items-center gap-3 p-2 rounded-3 bg-light bg-opacity-50">
                                <FaCar className="text-muted" />
                                <span className="text-dark fw-medium small">{vehiclePlate}</span>
                              </div>
                            )}
                            {remorqueName && (
                              <div className="d-flex align-items-center gap-3 p-2 rounded-3 bg-light bg-opacity-50">
                                <FaTrailer className="text-muted" />
                                <span className="text-dark fw-medium small">{remorqueName}</span>
                              </div>
                            )}
                          </div>


                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              className="flex-grow-1 rounded-pill fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                              onClick={() => handleAcceptSuggestion(p)}
                            >
                              <FaCheckCircle size={14} /> {t('common.confirm') || 'Confirmer'}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              className="rounded-circle border-0 d-flex align-items-center justify-content-center hover-danger p-0"
                              style={{ width: '2.5rem', height: '2.5rem' }}
                              onClick={() => handleRejectSuggestion(p.id)}
                              title="Refuser"
                            >
                              <FaTimes />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-4 border-top text-center bg-light bg-opacity-50">
              <Button variant="link" className="text-secondary text-decoration-none fw-medium" onClick={() => setShowSuggestionsModal(false)}>
                {t('common.close') || 'Ignorer les suggestions et configurer manuellement'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showAnalysisModal}
        onHide={() => setShowAnalysisModal(false)}
        centered
        className="acc-modal-premium"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <FaBrain className="text-primary" /> {t('common.aiAnalyseReport') || 'Rapport d\'Analyse IA'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4 px-4 pb-5">
          {analysisLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">{t('common.aiAnalyseLoading') || 'Analyse de la mission en cours...'}</p>
            </div>
          ) : (
            <div className="acc-analysis-content fade-in">
              <div
                className="p-4 rounded-4 bg-light border-start border-4 border-primary shadow-sm"
                style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7', fontSize: '1.05rem', color: '#2c3e50' }}
              >
                {analysisResult}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" className="rounded-pill px-4" onClick={() => setShowAnalysisModal(false)}>
            {t('common.close') || 'Fermer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Driver Busy Modal */}
      <Modal show={showDriverBusyModal} onHide={() => setShowDriverBusyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-warning">
            <FaExclamationTriangle className="me-2" />
            {t('common.attention') || 'Attention'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('common.driverBusyWarning') || 'Le chauffeur est actuellement dans une autre mission. Veuillez la mettre en pause ou la terminer pour libérer ses ressources.'}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDriverBusyModal(false)}>
            {t('common.close') || 'Fermer'}
          </Button>
        </Modal.Footer>
      </Modal>

    </DashboardLayout>
  );
}

export default AssignMission;